import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  heroSlides,
  services,
  countries,
  stats,
  testimonials,
  faqs,
  companyInfo,
  aboutContent,
} from "../data/mockData";
import {
  GraduationCap,
  Users,
  Building2,
  Briefcase,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Star,
  ChevronDown,
  Plane,
  Globe,
  FileCheck,
  BookOpen,
  Quote,
} from "lucide-react";

const iconMap = {
  GraduationCap,
  Users,
  Building2,
  Briefcase,
};

// Animated Counter
const Counter = ({ value, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const end = value;
    const duration = 2000;
    const step = (end / duration) * 16;
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, value]);

  return (
    <span ref={ref}>
      {count}
      {suffix}
    </span>
  );
};

// Fade in animation wrapper
const FadeInSection = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);

  // Auto slide
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[600px] overflow-hidden">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? "opacity-100 z-10" : "opacity-0 z-0"
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${slide.image})`,
                transform:
                  index === currentSlide ? "scale(1.05)" : "scale(1)",
                transition: "transform 8s ease-out",
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/40" />
          </div>
        ))}

        <div className="relative z-20 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 w-full">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="max-w-2xl"
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-0.5 bg-orange-500" />
                <span className="text-orange-400 text-sm font-medium tracking-wider uppercase">
                  Sun Lumia Overseas
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
                {heroSlides[currentSlide].title}{" "}
                <span className="text-orange-400">
                  {heroSlides[currentSlide].highlight}
                </span>
              </h1>
              <p className="text-lg text-slate-300 mb-8 max-w-lg">
                {heroSlides[currentSlide].subtitle}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="group px-7 py-3.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/30 flex items-center gap-2"
                >
                  {heroSlides[currentSlide].cta}
                  <ArrowRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </Link>
                <Link
                  to="/about"
                  className="px-7 py-3.5 border border-white/30 text-white rounded-lg font-medium hover:bg-white/10 transition-all duration-300"
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`transition-all duration-500 rounded-full ${
                i === currentSlide
                  ? "w-8 h-3 bg-orange-500"
                  : "w-3 h-3 bg-white/50 hover:bg-white/80"
              }`}
            />
          ))}
        </div>

        {/* Slide Arrows */}
        <button
          onClick={() =>
            setCurrentSlide(
              (currentSlide - 1 + heroSlides.length) % heroSlides.length
            )
          }
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all"
        >
          <ChevronLeft size={24} />
        </button>
        <button
          onClick={() =>
            setCurrentSlide((currentSlide + 1) % heroSlides.length)
          }
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-all"
        >
          <ChevronRight size={24} />
        </button>

        {/* 3D Floating Elements */}
        <motion.div
          animate={{ y: [0, -15, 0], rotateY: [0, 180, 360] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-32 right-20 z-20 hidden lg:block"
        >
          <div className="w-16 h-16 bg-orange-500/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-orange-500/30">
            <Plane size={28} className="text-orange-400" />
          </div>
        </motion.div>
        <motion.div
          animate={{ y: [0, 12, 0], rotate: [0, 10, -10, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute bottom-32 right-40 z-20 hidden lg:block"
        >
          <div className="w-14 h-14 bg-blue-500/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-blue-500/30">
            <Globe size={24} className="text-blue-400" />
          </div>
        </motion.div>
        <motion.div
          animate={{ y: [0, -10, 0], x: [0, 5, -5, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute top-48 right-60 z-20 hidden lg:block"
        >
          <div className="w-12 h-12 bg-green-500/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-green-500/30">
            <FileCheck size={20} className="text-green-400" />
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="relative -mt-16 z-30 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat) => (
              <div key={stat.id} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-orange-500">
                  <Counter value={stat.value} suffix={stat.suffix} />
                </div>
                <p className="text-slate-600 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeInSection>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=800&q=80"
                    alt="Students graduating"
                    className="w-full h-[400px] object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-orange-500 text-white rounded-2xl p-6 shadow-lg hidden md:block">
                  <div className="text-3xl font-bold">15+</div>
                  <div className="text-sm opacity-90">Years of Excellence</div>
                </div>
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-orange-100 rounded-2xl -z-10" />
              </div>
            </FadeInSection>

            <FadeInSection delay={0.2}>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-0.5 bg-orange-500" />
                  <span className="text-orange-500 text-sm font-semibold tracking-wider uppercase">
                    About Us
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                  Welcome to{" "}
                  <span className="text-orange-500">Sun Lumia Overseas</span>
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  {aboutContent.intro}
                </p>
                <p className="text-slate-600 leading-relaxed mb-6">
                  We offer free counselling to students aspiring to study in the
                  USA, United Kingdom, Australia, Singapore, New Zealand, Dubai,
                  Canada and Ireland.
                </p>
                <Link
                  to="/about"
                  className="group inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/25"
                >
                  Read More
                  <ArrowRight
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </Link>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-50 px-4">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-10 h-0.5 bg-orange-500" />
                <span className="text-orange-500 text-sm font-semibold tracking-wider uppercase">
                  What We Offer
                </span>
                <div className="w-10 h-0.5 bg-orange-500" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800">
                Our <span className="text-orange-500">Services</span>
              </h2>
            </div>
          </FadeInSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <FadeInSection key={service.id} delay={index * 0.1}>
                  <div className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-2" style={{ perspective: "1000px" }}>
                    <div className="h-48 overflow-hidden">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-6">
                      <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-orange-500 transition-colors duration-300">
                        <Icon
                          size={22}
                          className="text-orange-500 group-hover:text-white transition-colors duration-300"
                        />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-800 mb-2">
                        {service.title}
                      </h3>
                      <p className="text-slate-500 text-sm leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </FadeInSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Countries Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-10 h-0.5 bg-orange-500" />
                <span className="text-orange-500 text-sm font-semibold tracking-wider uppercase">
                  Destinations
                </span>
                <div className="w-10 h-0.5 bg-orange-500" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800">
                Countries We{" "}
                <span className="text-orange-500">Offer</span>
              </h2>
            </div>
          </FadeInSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {countries.map((country, index) => {
              const Wrapper = country.slug ? Link : "div";
              const wrapperProps = country.slug
                ? { to: `/country/${country.slug}` }
                : {};
              return (
                <FadeInSection key={country.id} delay={index * 0.1}>
                  <Wrapper
                    {...wrapperProps}
                    className="group relative rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 cursor-pointer hover:-translate-y-1 block"
                  >
                    <div className="h-64 overflow-hidden">
                      <img
                        src={country.image}
                        alt={country.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <img
                          src={country.flag}
                          alt={country.code}
                          className="w-8 h-5 object-cover rounded shadow"
                        />
                        <h3 className="text-xl font-bold text-white">
                          {country.name}
                        </h3>
                      </div>
                      <p className="text-slate-300 text-sm mb-3 line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        {country.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-orange-400 text-sm font-medium">
                          {country.universities}+ Universities
                        </span>
                        <div className="w-8 h-8 rounded-full bg-orange-500/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-0 translate-x-4">
                          <ArrowRight size={14} className="text-white" />
                        </div>
                      </div>
                    </div>
                  </Wrapper>
                </FadeInSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-900 px-4">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-10 h-0.5 bg-orange-500" />
                <span className="text-orange-400 text-sm font-semibold tracking-wider uppercase">
                  Testimonials
                </span>
                <div className="w-10 h-0.5 bg-orange-500" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                What Our <span className="text-orange-400">Students Say</span>
              </h2>
            </div>
          </FadeInSection>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <FadeInSection key={t.id} delay={i * 0.15}>
                <div className="bg-slate-800 rounded-2xl p-6 relative hover:bg-slate-800/80 transition-colors">
                  <Quote
                    size={32}
                    className="text-orange-500/20 absolute top-4 right-4"
                  />
                  <div className="flex gap-1 mb-3">
                    {[...Array(t.rating)].map((_, j) => (
                      <Star
                        key={j}
                        size={14}
                        className="text-yellow-400 fill-yellow-400"
                      />
                    ))}
                  </div>
                  <p className="text-slate-300 text-sm leading-relaxed mb-4">
                    "{t.text}"
                  </p>
                  <div>
                    <h4 className="text-white font-semibold text-sm">
                      {t.name}
                    </h4>
                    <p className="text-slate-400 text-xs">{t.course}</p>
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-10 h-0.5 bg-orange-500" />
                <span className="text-orange-500 text-sm font-semibold tracking-wider uppercase">
                  FAQs
                </span>
                <div className="w-10 h-0.5 bg-orange-500" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800">
                Frequently Asked{" "}
                <span className="text-orange-500">Questions</span>
              </h2>
            </div>
          </FadeInSection>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <FadeInSection key={faq.id} delay={i * 0.08}>
                <div
                  className={`border rounded-xl overflow-hidden transition-all duration-300 ${
                    openFaq === faq.id
                      ? "border-orange-200 bg-orange-50/50"
                      : "border-slate-200 hover:border-orange-200"
                  }`}
                >
                  <button
                    onClick={() =>
                      setOpenFaq(openFaq === faq.id ? null : faq.id)
                    }
                    className="w-full flex items-center justify-between p-5 text-left"
                  >
                    <span className="font-medium text-slate-800 pr-4">
                      {faq.question}
                    </span>
                    <ChevronDown
                      size={20}
                      className={`text-orange-500 flex-shrink-0 transition-transform duration-300 ${
                        openFaq === faq.id ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <motion.div
                    initial={false}
                    animate={{
                      height: openFaq === faq.id ? "auto" : 0,
                      opacity: openFaq === faq.id ? 1 : 0,
                    }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="px-5 pb-5 text-slate-600 text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </motion.div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto text-center">
          <FadeInSection>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Start Your Journey?
            </h2>
            <p className="text-orange-100 text-lg mb-8 max-w-2xl mx-auto">
              Take the first step towards your international education. Get free
              expert counselling from our experienced team.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="px-8 py-3.5 bg-white text-orange-600 rounded-lg font-semibold hover:bg-orange-50 transition-all duration-300 hover:shadow-lg flex items-center gap-2"
              >
                Contact Us Now
                <ArrowRight size={16} />
              </Link>
              <a
                href={`tel:${companyInfo.phone.replace(/\s/g, "")}`}
                className="px-8 py-3.5 border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-all duration-300"
              >
                Call {companyInfo.phone}
              </a>
            </div>
          </FadeInSection>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
